// Shared look for bordered form controls (Input, TextArea, Select, TagsInput) and the context
// `Form` / `FormItem` use to hand them a default size, status and id — as antd's Form does.
import { createContext, useContext } from "react";
import { cn } from "./cn";

export type ControlSize = "small" | "middle" | "large";
export type ControlStatus = "error" | "warning";

/** Set by `<Form size>`. */
export const ControlSizeContext = createContext<ControlSize | undefined>(undefined);

/** Set by `<FormItem>`: the label's `htmlFor` target and the validation status. */
export const FormItemContext = createContext<{
  id?: string;
  status?: ControlStatus;
  /** Id of the help / extra text under the control, for `aria-describedby`. */
  describedBy?: string;
}>({});

/** Resolve a control's size/status/id: explicit prop first, then the surrounding Form/FormItem. */
export const useControlDefaults = (props: { size?: ControlSize; status?: ControlStatus; id?: string }) => {
  const formSize = useContext(ControlSizeContext);
  const item = useContext(FormItemContext);

  return {
    size: props.size ?? formSize ?? "middle",
    status: props.status ?? item.status,
    id: props.id ?? item.id,
    /** Spread on the focusable element: links it to the help text and flags an error status. */
    aria: {
      "aria-describedby": item.describedBy,
      "aria-invalid": (props.status ?? item.status) === "error" ? (true as const) : undefined,
    },
  };
};

/** Border (line-input, the 3:1 control outline), surface, hover/focus ring — apply to the element that draws the box. */
export const controlBox = (status?: ControlStatus, disabled?: boolean) =>
  cn(
    "rounded-md border border-noa-input bg-noa text-noa transition-colors",
    !disabled &&
      "hover:border-noa-primary-hover focus-within:border-noa-focus-ring focus-within:ring-2 focus-within:ring-noa-primary-bg",
    disabled && "cursor-not-allowed bg-noa-hover text-noa-disabled",
    status === "error" &&
      "border-noa-danger-strong hover:border-noa-danger-strong focus-within:border-noa-danger-strong",
    status === "warning" &&
      "border-noa-warning-strong hover:border-noa-warning-strong focus-within:border-noa-warning-strong"
  );

/** Fixed-height single-line controls. */
export const controlHeight: Record<ControlSize, string> = {
  small: "h-6 px-[7px] text-sm",
  middle: "h-8 px-[11px] text-sm",
  large: "h-10 px-[11px] text-base",
};

export const INNER_INPUT =
  "min-w-0 flex-1 border-0 bg-transparent p-0 text-inherit outline-none placeholder:text-noa-disabled disabled:cursor-not-allowed";
